import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { LuX, LuRefreshCw } from 'react-icons/lu';

const ChecklistSection = ({ items, loadError, onAddItem, onToggleChecked, onRemoveItem, onRetryLoad, isApplyingTemplate }) => {
    const [newItemTitle, setNewItemTitle] = useState('');
    const [newItemRequired, setNewItemRequired] = useState(true);

    const requiredItems = items.filter(i => i.isRequired);
    const requiredDoneCount = requiredItems.filter(i => i.isChecked).length;

    const handleAdd = () => {
        const title = newItemTitle.trim();
        if (!title) return;
        onAddItem({ title, isRequired: newItemRequired });
        setNewItemTitle('');
        setNewItemRequired(true);
    };

    // This section sits inside the Create Task form, and a form inside a form is not valid. So it has
    // no form of its own: Enter is caught here, and kept from submitting the surrounding form.
    const handleTitleKeyDown = (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        handleAdd();
    };

    return (
        <div className="form-group checklist-section">
            <div className="attachments-header">
                <label>Checklist</label>
                {isApplyingTemplate && (
                    <span className="checklist-template-loading">
                        <LuRefreshCw className="checklist-template-spinner" /> Loading checklist...
                    </span>
                )}
                {!isApplyingTemplate && !loadError && requiredItems.length > 0 && (
                    <span className="attachments-count">{requiredDoneCount}/{requiredItems.length} required completed</span>
                )}
            </div>

            {loadError ? (
                <div className="checklist-load-error">
                    <p className="checklist-error-text">{loadError}</p>
                    <button type="button" className="btn-text" onClick={onRetryLoad}>Retry</button>
                </div>
            ) : items.length === 0 ? (
                <p className="no-attachments-text">No checklist items yet.</p>
            ) : (
                <ul className="checklist-item-list">
                    {items.map(item => (
                        <li key={item.id} className="checklist-item-row">
                            <label className="checklist-item-label">
                                <input
                                    type="checkbox"
                                    checked={item.isChecked}
                                    onChange={() => onToggleChecked(item)}
                                />
                                <span className={item.isChecked ? 'checklist-item-done' : ''}>{item.title}</span>
                                {item.isRequired && <span className="checklist-required-badge">*</span>}
                            </label>
                            <button
                                type="button"
                                className="checklist-remove-btn"
                                aria-label={`Remove ${item.title}`}
                                onClick={() => onRemoveItem(item.id)}
                            >
                                <LuX />
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {!loadError && (
                <div className="checklist-add-form">
                    <div className="checklist-add-row">
                        <input
                            type="text"
                            value={newItemTitle}
                            onChange={(e) => setNewItemTitle(e.target.value)}
                            onKeyDown={handleTitleKeyDown}
                            placeholder="Add checklist item..."
                            aria-label="New checklist item title"
                        />
                        <button type="button" className="checklist-add-btn" onClick={handleAdd}>Add</button>
                    </div>
                    <label className="checklist-required-toggle">
                        <input
                            type="checkbox"
                            checked={newItemRequired}
                            onChange={(e) => setNewItemRequired(e.target.checked)}
                        />
                        Required
                    </label>
                </div>
            )}
        </div>
    );
};

ChecklistSection.propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        isRequired: PropTypes.bool,
        isChecked: PropTypes.bool,
    })).isRequired,
    loadError: PropTypes.string,
    onAddItem: PropTypes.func.isRequired,
    onToggleChecked: PropTypes.func.isRequired,
    onRemoveItem: PropTypes.func.isRequired,
    onRetryLoad: PropTypes.func.isRequired,
    isApplyingTemplate: PropTypes.bool,
};

export default ChecklistSection;
